import Autoplay from 'embla-carousel-autoplay';
import { useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { onboardingFeatureGroups, kizeoUseCases } from './data';
import { FeatureGroup } from './feature-group';
import { UseCase } from './use-case';

import { Button } from '@/components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselKizeoUseCaseDots,
} from '@/components/ui/carousel';

interface OnboardingProps {
  onStart: () => void;
}

const Onboarding = ({ onStart }: OnboardingProps) => {
  const { t } = useTranslation();

  // Force html and body to 100% height for the iframe
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      html, body, #root {
        height: 100% !important;
        margin: 0;
        padding: 0;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6 md:mb-2">
          <img
            src="/img/custom/logo.svg"
            alt="Khub"
            className="h-10 3xl:h-12"
          />
          <p className="text-2xl 3xl:text-3xl font-semibold">Khub</p>
        </div>

        <div className="flex flex-col items-center">
          <h1 className="text-2xl 3xl:text-3xl font-medium text-center">
            <Trans
              i18nKey="Discover the power of simple, flexible, and intuitive automation"
              components={{ highlight: <span className="text-primary" /> }}
            />
          </h1>
          <p className="text-center mt-1 3xl:mt-2 3xl:text-lg">
            {t('Create your flows without coding and focus on what matters.')}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full flex flex-wrap justify-center items-center gap-6 3xl:gap-12">
          <div className="w-full md:w-fit flex flex-col gap-8 md:gap-4 3xl:gap-8 items-center">
            <div className="w-full md:w-fit flex flex-col gap-8 md:gap-4 3xl:gap-8">
              {onboardingFeatureGroups.map((group) => (
                <FeatureGroup key={group.name} {...group} />
              ))}
            </div>
            <Button
              className="3xl:text-base 3xl:px-8 3xl:py-3 3xl:h-11"
              onClick={onStart}
            >
              {t('Create my first flow')}
            </Button>
          </div>

          <div className="bg-[#011439] text-white rounded-2xl flex flex-col gap-8 3xl:gap-16 items-start w-full p-4 sm:p-6 sm:w-[618px] 3xl:w-[700px] flex-shrink-0">
            <img
              src="/img/custom/kizeo/forms-brand-transparent-color-light.svg"
              alt="Kizeo Forms"
              className="h-10 3xl:h-12"
            />

            <div className="w-full">
              <p className="text-lg 3xl:text-xl font-medium">
                {t(
                  'What if Khub automated the repetitive tasks in your daily work?'
                )}
              </p>
              <p className="text-sm 3xl:text-base 3xl:mt-1 font-light">
                {t('Connect your tools. Streamline your processes. Save time.')}
              </p>
            </div>

            <div className="w-full px-4 sm:px-12 xl:px-16">
              <Carousel
                plugins={[
                  Autoplay({
                    delay: 4000,
                    stopOnInteraction: true,
                  }),
                ]}
                opts={{ align: 'start', loop: true }}
              >
                <CarouselContent className="w-full">
                  {kizeoUseCases.map((kizeoUseCase) => (
                    <CarouselItem key={kizeoUseCase.title}>
                      <UseCase {...kizeoUseCase} onStart={onStart} />
                    </CarouselItem>
                  ))}
                </CarouselContent>

                <CarouselPrevious
                  variant="basic"
                  className="hidden sm:flex top-[40%] bg-[#55c96b]/30 text-[#55c96b] enabled:hover:bg-[#55c96b]/40"
                />
                <CarouselNext
                  variant="basic"
                  className="hidden sm:flex top-[40%] bg-[#55c96b]/30 text-[#55c96b] enabled:hover:bg-[#55c96b]/40"
                />
                <CarouselKizeoUseCaseDots />
              </Carousel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Onboarding };
